<template>
  <div class="product-gallery">
    <div class="product-gallery__desktop">
      <div>
        <div class="btn-container">
          <button
            v-show="shouldDisplayArrows && firstThumbInView > 0"
            :aria-label="upArrowAlt"
            class="product-gallery--button product-gallery--button-prev"
            @click="displayPrevThumbnail()"
          >
            <svg class="icon">
              <use xlink:href="#chevron-right" />
            </svg>
          </button>
        </div>

        <div ref="thumbnails" class="thumbnails">
          <slot name="thumbnails" />
        </div>

        <div class="btn-container">
          <button
            v-show="shouldDisplayArrows && hasMoreThumbnails"
            :aria-label="downArrowAlt"
            class="product-gallery--button product-gallery--button-next"
            @click="displayNextThumbnail()"
          >
            <svg class="icon">
              <use xlink:href="#chevron-right" />
            </svg>
          </button>
        </div>
      </div>
      <div ref="gallery" class="gallery">
        <slot />
      </div>
    </div>

    <div class="product-gallery__mobile">
      <div ref="mobileGallery" class="product-gallery__mobile-wrapper">
        <slot name="mobile" />
      </div>
      <div class="product-gallery__mobile-hint">
        <svg
          class="product-gallery__mobile-hint-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
          <line x1="11" y1="8" x2="11" y2="14" />
          <line x1="8" y1="11" x2="14" y2="11" />
        </svg>
        {{ tapToZoomText }}
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'ProductGallery',
    props: {
      upArrowAlt: {
        type: String,
        required: false,
        default: 'Up Arrow',
      },
      downArrowAlt: {
        type: String,
        required: false,
        default: 'Down Arrow',
      },
      maxDisplayedThumbnails: {
        type: Number,
        required: false,
        default: 6,
      },
      tapToZoomText: {
        type: String,
        required: false,
        default: 'Tap to zoom',
      },
    },
    data() {
      return {
        thumbnailList: null,
        bigMediaList: null,
        activeIndex: null,
        firstThumbInView: 0,
        thumbnailHeight: 0,
        thumbnailCount: 0,
        isZoomed: false,
        zoomScale: 2.5,
        mouseX: 0,
        mouseY: 0,
        mobileImages: null,
        isMobileZoomed: false,
        mobileScale: 1,
        mobileTranslateX: 0,
        mobileTranslateY: 0,
        isDragging: false,
        dragStartX: 0,
        dragStartY: 0,
        initialTranslateX: 0,
        initialTranslateY: 0,
        hasMoved: false,
      };
    },
    TAP_THRESHOLD: 10,
    computed: {
      firstThumbInViewTop() {
        return (
          this.thumbnailList[this.firstThumbInView].offsetTop - this.thumbnailList[0].offsetTop
        );
      },
      hasMoreThumbnails() {
        return this.thumbnailCount > this.maxDisplayedThumbnails + this.firstThumbInView;
      },
      shouldDisplayArrows() {
        return this.thumbnailCount > this.maxDisplayedThumbnails;
      },
    },
    watch: {
      activeIndex(newActive) {
        this.removeActiveClassFromGallery();
        this.bigMediaList[newActive].classList.add('active');
        this.exitZoomMode();
        this.setupImageZoomHandlers();
      },
    },
    mounted() {
      if (document.getElementById('main-container')) {
        document.getElementById('main-container').style.overflow = 'inherit';
      }
      window.addEventListener('resize', this.setDisplayedThumbnails);

      this.$nextTick().then(() => {
        this.thumbnailList = this.$refs.thumbnails.children[0].children;
        this.thumbnailCount = this.thumbnailList.length;
        this.bigMediaList = this.$refs.gallery.children[0].children;
        this.setGalleryIds();
        this.addEventListenersToThumbnails();
        this.thumbnailList[0].classList.add('active');
        this.activeIndex = 0;
        this.setDisplayedThumbnails();
        this.setupImageZoomHandlers();
        this.setupMobileZoomHandlers();
      });
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.setDisplayedThumbnails);
      this.cleanupMobileZoomHandlers();
    },
    methods: {
      addEventListenersToThumbnails() {
        for (let i = 0; i < this.thumbnailList.length; i++) {
          const element = this.thumbnailList[i];
          element.addEventListener('click', this.clickEvent);
          element.setAttribute('aria-controls', `product-gallery--${i}`);
          element.setAttribute('aria-expanded', i ? 'false' : 'true');
        }
      },
      setGalleryIds() {
        for (let i = 0; i < this.bigMediaList.length; i++) {
          const element = this.bigMediaList[i];
          element.setAttribute('id', `product-gallery--${i}`);
        }
      },
      clickEvent(e) {
        this.removeActiveClassFromThumbnails();
        e.target.classList.add('active');
        e.target.setAttribute('aria-expanded', 'true');
        this.$emit('pauseVideo');
        this.activeIndex = this.getActiveThumbnail();
      },
      setDisplayedThumbnails() {
        this.thumbnailHeight = this.thumbnailList[0] ? this.thumbnailList[0].offsetHeight : null;
        const maxHeight =
          this.thumbnailList[this.maxDisplayedThumbnails] && this.$refs.thumbnails
            ? `${this.thumbnailList[this.maxDisplayedThumbnails].offsetTop -
                this.$refs.thumbnails.offsetTop}px`
            : 'none';
        this.$refs.thumbnails.style.maxHeight = maxHeight;
      },
      removeActiveClassFromThumbnails() {
        for (let i = 0; i < this.thumbnailList.length; i++) {
          const element = this.thumbnailList[i];
          element.classList.remove('active');
          element.setAttribute('aria-expanded', 'false');
        }
      },
      removeActiveClassFromGallery() {
        for (let i = 0; i < this.bigMediaList.length; i++) {
          const element = this.bigMediaList[i];
          element.classList.remove('active');
        }
      },
      getActiveThumbnail() {
        for (let i = 0; i < this.thumbnailList.length; i++) {
          const element = this.thumbnailList[i];
          if (element.classList.contains('active')) {
            return i;
          }
        }
        return 0;
      },
      displayNextThumbnail() {
        if (!this.hasMoreThumbnails) {
          return;
        }

        this.firstThumbInView++;
        for (let i = 0; i < this.thumbnailList.length; i++) {
          const element = this.thumbnailList[i];
          element.style.transform = `translateY(-${this.firstThumbInViewTop}px)`;
        }
      },
      displayPrevThumbnail() {
        this.firstThumbInView--;
        for (let i = 0; i < this.thumbnailList.length; i++) {
          const element = this.thumbnailList[i];
          element.style.transform = `translateY(${-this.firstThumbInViewTop}px)`;
        }
      },
      setupImageZoomHandlers() {
        if (!this.bigMediaList || !this.bigMediaList[this.activeIndex]) {
          return;
        }

        const activeItem = this.bigMediaList[this.activeIndex];
        const img = activeItem.querySelector('img:not(.tag)');

        if (img) {
          img.removeEventListener('click', this.handleImageClick);
          img.removeEventListener('mousemove', this.handleImageMouseMove);
          img.addEventListener('click', this.handleImageClick);
          img.addEventListener('mousemove', this.handleImageMouseMove);
        }
      },
      handleImageClick(e) {
        const activeItem = this.bigMediaList[this.activeIndex];

        if (this.isZoomed) {
          this.exitZoomMode();
        } else {
          this.enterZoomMode(activeItem, e);
        }
      },
      enterZoomMode(activeItem, e) {
        this.isZoomed = true;
        activeItem.classList.add('gallery__item--zoomed');

        const img = activeItem.querySelector('img:not(.tag)');
        if (img) {
          img.style.cursor = 'zoom-out';
          this.updateZoomPosition(e);
        }
      },
      exitZoomMode() {
        if (!this.isZoomed) {
          return;
        }

        this.isZoomed = false;

        if (this.bigMediaList && this.bigMediaList[this.activeIndex]) {
          const activeItem = this.bigMediaList[this.activeIndex];
          activeItem.classList.remove('gallery__item--zoomed');

          const img = activeItem.querySelector('img:not(.tag)');
          if (img) {
            img.style.cursor = 'zoom-in';
            img.style.transform = '';
            img.style.transformOrigin = '';
          }
        }
      },
      handleImageMouseMove(e) {
        if (!this.isZoomed) {
          return;
        }

        this.updateZoomPosition(e);
      },
      updateZoomPosition(e) {
        const img = e.currentTarget;
        const rect = img.getBoundingClientRect();

        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        img.style.transformOrigin = `${x}% ${y}%`;
        img.style.transform = `scale(${this.zoomScale})`;
      },
      setupMobileZoomHandlers() {
        if (!this.$refs.mobileGallery) {
          return;
        }

        this.$nextTick(() => {
          const images = this.$refs.mobileGallery.querySelectorAll('img:not(.tag)');
          this.mobileImages = Array.from(images);

          this.mobileImages.forEach(img => {
            img.style.transition = 'transform 0.3s ease-out';
            img.addEventListener('touchstart', this.handleMobileTouchStart, {
              passive: false,
              capture: true,
            });
            img.addEventListener('touchmove', this.handleMobileTouchMove, {
              passive: false,
              capture: true,
            });
            img.addEventListener('touchend', this.handleMobileTouchEnd, {
              passive: false,
              capture: true,
            });
          });
        });
      },
      cleanupMobileZoomHandlers() {
        if (this.mobileImages) {
          this.mobileImages.forEach(img => {
            img.removeEventListener('touchstart', this.handleMobileTouchStart);
            img.removeEventListener('touchmove', this.handleMobileTouchMove);
            img.removeEventListener('touchend', this.handleMobileTouchEnd);
          });
        }
      },
      handleMobileTouchStart(e) {
        const img = e.currentTarget;

        if (e.touches.length === 1) {
          this.hasMoved = false;
          this.dragStartX = e.touches[0].clientX;
          this.dragStartY = e.touches[0].clientY;

          if (this.isMobileZoomed && this.mobileScale > 1) {
            e.preventDefault();
            e.stopPropagation();
            this.isDragging = true;
            this.initialTranslateX = this.mobileTranslateX;
            this.initialTranslateY = this.mobileTranslateY;
            img.style.transition = 'none';
          }
        }
      },
      handleMobileTouchMove(e) {
        const img = e.currentTarget;

        if (e.touches.length === 1) {
          const deltaX = e.touches[0].clientX - this.dragStartX;
          const deltaY = e.touches[0].clientY - this.dragStartY;
          const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

          if (distance > this.$options.TAP_THRESHOLD) {
            this.hasMoved = true;
          }

          if (this.isDragging && this.isMobileZoomed) {
            e.preventDefault();
            e.stopPropagation();

            this.mobileTranslateX = this.initialTranslateX + deltaX;
            this.mobileTranslateY = this.initialTranslateY + deltaY;

            const maxTranslate = (this.mobileScale - 1) * 100;
            this.mobileTranslateX = Math.max(
              -maxTranslate,
              Math.min(maxTranslate, this.mobileTranslateX)
            );
            this.mobileTranslateY = Math.max(
              -maxTranslate,
              Math.min(maxTranslate, this.mobileTranslateY)
            );

            this.applyMobileTransform(img);
          }
        }
      },
      handleMobileTouchEnd(e) {
        const img = e.currentTarget;

        if (!this.hasMoved && e.touches.length === 0) {
          e.preventDefault();
          e.stopPropagation();
          this.handleMobileTap(e, img);
        } else if (this.isMobileZoomed && this.hasMoved) {
          e.stopPropagation();
        }

        this.isDragging = false;
        this.hasMoved = false;
        img.style.transition = 'transform 0.3s ease-out';
      },
      handleMobileTap(e, img) {
        if (this.isMobileZoomed) {
          this.resetMobileZoom(img);
        } else {
          this.mobileScale = 2.5;
          this.isMobileZoomed = true;

          const rect = img.getBoundingClientRect();
          const touch = e.changedTouches[0];
          const x = touch.clientX - rect.left;
          const y = touch.clientY - rect.top;

          const percentX = (x / rect.width) * 100;
          const percentY = (y / rect.height) * 100;

          this.mobileTranslateX = (50 - percentX) * (this.mobileScale - 1) * 2;
          this.mobileTranslateY = (50 - percentY) * (this.mobileScale - 1) * 2;

          this.applyMobileTransform(img);
        }
      },
      resetMobileZoom(img) {
        this.isMobileZoomed = false;
        this.mobileScale = 1;
        this.mobileTranslateX = 0;
        this.mobileTranslateY = 0;
        img.style.transform = 'scale(1) translate(0, 0)';
      },
      applyMobileTransform(img) {
        img.style.transform = `scale(${this.mobileScale}) translate(${this.mobileTranslateX}px, ${this.mobileTranslateY}px)`;
      },
    },
  };
</script>

<style src="./ProductGallery.scss" lang="scss"></style>
